import { merge } from 'lodash';
import colors from '@/themes/colors/colors';
import { darkenColor, lightenColor } from '@/themes/colorUtils/colorUtils';
import themeInterface from '@/themes/themes/themeInterface';

const darkTheme = {
  name: 'dark',
  background: {
    bg: lightenColor(colors.black, 0.1),
    text: colors.white,
    textDiscrete: darkenColor(colors.white, 0.25),
    textAltColor: colors.white,
    variant: {
      bg: lightenColor(colors.black, 0.3),
      text: colors.red,
      textDiscrete: colors.red,
      textAltColor: colors.red,
    },
  },
  border: {
    color: darkenColor(colors.suvaGrey, 0.4),
    variant: {
      color: darkenColor(colors.suvaGrey, 0.5),
    },
  },
  shadow: {
    color: colors.black,
    variant: {
      color: colors.red,
    },
  },
  primary: {
    bg: lightenColor(colors.black, 0.2),
    text: colors.white,
    textDiscrete: darkenColor(colors.white, 0.25),
    textAltColor: lightenColor(colors.norticBlue, 0.6),
    variant: {
      bg: lightenColor(colors.black, 0.13),
      text: colors.white,
      textDiscrete: colors.red,
      textAltColor: lightenColor(colors.norticBlue, 0.4),
    },
  },
  accent: {
    bg: colors.norticBlue,
    text: colors.white,
    textDiscrete: colors.white,
    textAltColor: colors.white,
    variant: {
      bg: lightenColor(colors.norticBlue, 0.1),
      text: colors.white,
      textDiscrete: colors.white,
      textAltColor: colors.white,
    },
  },
  accentComplementary: {
    bg: colors.internationalOrange,
    text: colors.white,
    textDiscrete: colors.white,
    textAltColor: colors.black,
    variant: {
      bg: darkenColor(colors.internationalOrange, 0.05),
      text: colors.white,
      textDiscrete: colors.red,
      textAltColor: lightenColor(colors.norticBlue, 0.4),
    },
  },
  success: {
    bg: darkenColor(colors.forestGreen, 0.75),
    text: colors.black,
    textDiscrete: colors.white,
    textAltColor: lightenColor(colors.forestGreen, 0.2),
    variant: {
      bg: darkenColor(colors.forestGreen, 0.65),
      text: colors.black,
      textDiscrete: colors.white,
      textAltColor: darkenColor(colors.forestGreen, 0.1),
    },
  },
  danger: {
    bg: darkenColor(colors.sunsetOrange, 0.8),
    text: colors.black,
    textDiscrete: colors.white,
    textAltColor: lightenColor(colors.sunsetOrange, 0.2),
    variant: {
      bg: darkenColor(colors.sunsetOrange, 0.7),
      text: colors.black,
      textDiscrete: colors.white,
      textAltColor: colors.sunsetOrange,
    },
  },
  warn: {
    bg: darkenColor(colors.darkTangerine, 0.8),
    text: colors.black,
    textDiscrete: colors.white,
    textAltColor: darkenColor(colors.darkTangerine, 0.1),
    variant: {
      bg: darkenColor(colors.darkTangerine, 0.7),
      text: colors.black,
      textDiscrete: colors.white,
      textAltColor: darkenColor(colors.darkTangerine, 0.1),
    },
  },
  info: {
    bg: darkenColor(colors.deepSkyBlue, 0.8),
    text: colors.black,
    textDiscrete: colors.white,
    textAltColor: darkenColor(colors.deepSkyBlue, 0.1),
    variant: {
      bg: lightenColor(colors.suvaGrey, 0.7),
      text: colors.black,
      textDiscrete: colors.white,
      textAltColor: darkenColor(colors.suvaGrey, 0.3),
    },
  },
  contrast: {
    bg: colors.white,
    text: colors.norticBlue,
    textDiscrete: colors.suvaGrey,
    textAltColor: colors.black,
    variant: {
      bg: darkenColor(colors.white, 0.2),
      text: colors.norticBlue,
      textDiscrete: colors.suvaGrey,
      textAltColor: colors.black,
    },
  },
  menu: {
    moduleCategory: {
      text: colors.white,
    },
    module: {
      text: darkenColor(colors.white, 0.1),
      highlightBg: lightenColor(colors.black, 0.3),
      activeBg: lightenColor(colors.black, 0.25),
    },
    subModule: {
      text: darkenColor(colors.white, 0.1),
      activeText: lightenColor(colors.norticBlue, 0.4),
    },
  },
  subMenu: {
    opacity: '0.4',
  },
  input: {
    bg: lightenColor(colors.black, 0.15),
    border: darkenColor(colors.suvaGrey, 0.8),
    text: colors.white,
    active: {
      bg: '#2e2f39',
      border: lightenColor(colors.norticBlue, 0.3),
    },
    danger: {
      bg: '#332222',
      border: darkenColor(colors.sunsetOrange, 0.5),
    },
    warn: {
      bg: darkenColor(colors.darkTangerine, 0.8),
      border: darkenColor(colors.darkTangerine, 0.5),
    },
    hover: {
      bg: lightenColor(colors.black, 0.17),
      border: darkenColor(colors.suvaGrey, 0.8),
    },
    alt: {
      text: darkenColor(colors.white, 0.5),
    },
  },
  misc: {
    salesTools: {
      socialSelling: colors.deepSkyBlue,
      trafficBooster: colors.sunsetOrange,
      outdoorAds: colors.darkTangerine,
      webAds: colors.blueViolet,
    },
    landingPage: {
      createEventIcon: colors.forestGreen,
      comfortPackageIcon: colors.deepSkyBlue,
      supportIcon: colors.wildWatermelon,
      myEventsIcon: colors.royalBlue,
      salesToolsIcon: colors.blueViolet,
      addProductIcon: colors.mountainMeadow,
      norticLivePaymentsIcon: colors.norticBlue,
      discoverNorticApps: colors.norticBlue,
    },
    productVariantColors: {
      0: colors.blueViolet,
      1: darkenColor(colors.darkTangerine, 0.1),
      2: colors.forestGreen,
    },
  },
};

export default Object.freeze(merge({}, themeInterface, darkTheme));
